import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';
import type { QueryObserverResult } from '@tanstack/vue-query';

type TranslateParams = { path: string };

// --- Section: Error Identification Functions (Boolean Returns) ---
// We should move this section into a separate file: errorConditions.ts
export function isMissingTrack(error: Error | null): boolean {
  return (
    isAxiosError(error) &&
    error.response?.status === 500 &&
    (error.response.data as { errorCode: string } | undefined)?.errorCode === 'MISSING_TRACK'
  );
}

export function getIsActiveReturnUncomputable(error: Error | null): boolean {
  return (
    isAxiosError(error) &&
    error.response?.status === 404 &&
    (error.response.data as { message: string } | undefined)?.message === 'Active return track could not be computed'
  );
}

export function isGatewayTimeout(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 504;
}

export function isNegativeTrack(error: Error | null): boolean {
  return (
    isAxiosError(error) &&
    error.response?.status === 500 &&
    ((error.response.data as { message: string } | undefined)?.message.includes('Negative track values') ?? false)
  );
}

export function areAllQueriesMissingTrack(queries: QueryObserverResult<unknown, Error>[]): boolean {
  return !!queries.length && queries.every((query) => isMissingTrack(query.error));
}

// --- Section: Error Message Functions (Return TranslateParams) ---
/**
 * Handles Axios errors and returns the appropriate error message.
 * Consolidates all specific error checks into a single function.
 * Mainly used for error overlay in charts
 */
export function getAxiosErrorMessage(error: AxiosError): TranslateParams {
  if (getIsActiveReturnUncomputable(error)) {
    return { path: 'GLOBAL.ERROR.ERROR_COMPUTE_ACTIVE_RETURN_TRACK' };
  }

  if (isMissingTrack(error)) {
    return { path: 'GLOBAL.ERROR.MISSING_TRACK' };
  }

  if (isGatewayTimeout(error)) {
    return { path: 'GLOBAL.ERROR.TIMEOUT' };
  }

  // Default case for unmatched errors
  return { path: 'GLOBAL.ERROR.ERROR_LOADING_DATA' };
}
